// DocumentSearch — top-header document search container (CTSK-493
// §5.5; design doc §3, §6). Ties the input, dropdown, scope chip and
// empty/error states to `useDocumentSearch`, and owns the dropdown state
// machine: open/closed, keyboard focus index, nest-scope override.
//
// Keyboard model (design doc §3.4):
//   /       focus the input (useGlobalShortcut; ignored inside text inputs)
//   ↑ / ↓   move the focus ring over the dropdown's visual order (wraps)
//   Enter   focused row → open that document; no row → /documents?q=<query>
//   Esc     close the dropdown, keep the text
//   Tab     close the dropdown, let focus move on naturally
//
// Nest-filter carry-through (§3.5): the Documents page keeps its active
// nest in the URL (`/documents?nest=<slug>` — Router → DocumentLibrary),
// so this component reads the SAME source of truth from `currentPath`.
// "search all nests" lifts the scope for this search only; a navigation
// that changes ?nest= re-arms it.
//
// Mounted by AppShell only when `features.documentSearch.enabled`.

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { DocumentSearchResult as SearchResult } from '@captain-tasko/shared';
import { DocumentSearchInput } from './DocumentSearchInput.js';
import { DocumentSearchDropdown } from './DocumentSearchDropdown.js';
import { DocumentSearchScope } from './DocumentSearchScope.js';
import { DocumentSearchEmpty } from './DocumentSearchEmpty.js';
import { DocumentSearchError } from './DocumentSearchError.js';
import { groupByNest } from './groupByNest.js';
import { optionIdFor } from './highlight.js';
import { useDocumentSearch } from './hooks/useDocumentSearch.js';
import { useGlobalShortcut } from './hooks/useGlobalShortcut.js';
import type { LabelKind } from './labelChips.js';

export const DOCUMENT_SEARCH_LISTBOX_ID = 'document-search-results';

export interface DocumentSearchProps {
  /** Current route incl. query string (App → AppShell → here). */
  currentPath: string;
  onNavigate: (path: string) => void;
  /** Register the `/` shortcut. Default true (the mount is flag-gated). */
  shortcutEnabled?: boolean;
}

/** `/documents?nest=<slug>` → slug; anything else → null. */
export function nestScopeFromPath(currentPath: string): string | null {
  try {
    const url = new URL(currentPath, 'http://localhost');
    const path = url.pathname.replace(/\/$/, '') || '/';
    if (path !== '/documents') return null;
    const nest = url.searchParams.get('nest');
    return nest && nest.trim() ? nest.trim() : null;
  } catch {
    return null;
  }
}

/**
 * The nest scope the user actually sees. The `currentPath` prop is the
 * App's route state, but DocumentLibrary mirrors chip clicks into the URL
 * with `history.replaceState` (ctsk-056) — no popstate, so the prop goes
 * stale until the next real navigation. The URL is the declared source
 * of truth, so when the live pathname matches the prop's we read the
 * live query string; otherwise (tests, mid-navigation) the prop wins.
 */
export function resolvePageNest(currentPath: string): string | null {
  const fromProp = nestScopeFromPath(currentPath);
  if (typeof window === 'undefined') return fromProp;
  try {
    const propPath = new URL(currentPath, 'http://localhost').pathname.replace(/\/$/, '') || '/';
    const livePath = window.location.pathname.replace(/\/$/, '') || '/';
    if (propPath !== livePath) return fromProp;
    return nestScopeFromPath(window.location.pathname + window.location.search);
  } catch {
    return fromProp;
  }
}

/** CTSK-507: an optional label filter rides along (a chip clicked in the dropdown). */
export function fullSearchPath(query: string, label?: { kind: LabelKind; value: string }): string {
  return `/documents?q=${encodeURIComponent(query)}${label ? `&${label.kind}=${encodeURIComponent(label.value)}` : ''}`;
}

export function DocumentSearch({ currentPath, onNavigate, shortcutEnabled = true }: DocumentSearchProps) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [scopeCleared, setScopeCleared] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const [pageNest, setPageNest] = useState<string | null>(() => resolvePageNest(currentPath));
  const syncScope = useCallback(() => { setPageNest(resolvePageNest(currentPath)); }, [currentPath]);
  useEffect(() => { syncScope(); }, [syncScope]);
  // A nest change (route or chip) re-arms the scope.
  useEffect(() => { setScopeCleared(false); }, [pageNest]);
  const nest = scopeCleared ? null : pageNest;

  const search = useDocumentSearch({ query: value, nest });
  const { flat } = useMemo(() => groupByNest(search.results), [search.results]);

  // New result set → nothing focused yet (mirrors UserTypeahead, but we
  // start at -1 so a bare Enter still means "full search").
  useEffect(() => { setFocusedIndex(-1); }, [search.results]);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  useGlobalShortcut('/', focusInput, { enabled: shortcutEnabled });

  // Outside click closes (standard picker pattern).
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const hasResults = search.results.length > 0;
  const showEmpty = search.active && !search.isLoading && !search.isError && search.data !== undefined && !hasResults;
  const showError = search.active && search.isError;
  const showDropdown = open && search.active && (hasResults || showEmpty || showError);
  const focused = focusedIndex >= 0 ? flat[focusedIndex] : undefined;
  // Chip identity: the server echoes the applied nest in meta.nest; until
  // the first response lands (or on NEST_NOT_FOUND) fall back to the slug.
  const scopeNest = nest
    ? (search.data?.meta.nest ?? { slug: nest, name: nest, code: nest.toUpperCase() })
    : null;

  const select = useCallback((r: SearchResult) => {
    setOpen(false);
    onNavigate(r.url || `/documents/${r.id}`);
  }, [onNavigate]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!search.active) return;
        e.preventDefault();
        if (!open) { setOpen(true); }
        if (flat.length === 0) return;
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        setFocusedIndex((i) => {
          if (i < 0) return dir === 1 ? 0 : flat.length - 1;
          return (i + dir + flat.length) % flat.length;
        });
        return;
      }
      case 'Enter': {
        if (e.nativeEvent.isComposing) return;
        if (showDropdown && focused) {
          e.preventDefault();
          select(focused);
          return;
        }
        if (search.query.length >= 2 || value.trim().length >= 2) {
          e.preventDefault();
          setOpen(false);
          onNavigate(fullSearchPath(value.trim()));
        }
        return;
      }
      case 'Escape': {
        if (open) {
          e.preventDefault();
          setOpen(false);
          setFocusedIndex(-1);
        }
        return;
      }
      case 'Tab': {
        // Close; do NOT preventDefault — focus moves to the next header element.
        setOpen(false);
        return;
      }
      default:
        return;
    }
  }

  const announcement = !search.active
    ? ''
    : showError
      ? 'Search temporarily unavailable'
      : search.data
        ? `${search.data.data.total} result${search.data.data.total === 1 ? '' : 's'} for ${search.query}`
        : '';

  return (
    <div ref={rootRef} className="doc-search" data-testid="document-search">
      <DocumentSearchInput
        value={value}
        onChange={(next) => { syncScope(); setValue(next); setOpen(true); }}
        onKeyDown={onKeyDown}
        onFocus={() => { syncScope(); setOpen(true); }}
        // Blur from a click inside the dropdown is prevented at mousedown;
        // any other focus loss closes (design doc §3.2).
        onBlur={() => setOpen(false)}
        inputRef={inputRef}
        listboxId={DOCUMENT_SEARCH_LISTBOX_ID}
        expanded={showDropdown && hasResults}
        activeDescendant={focused ? optionIdFor(focused) : undefined}
        loading={search.isFetching}
      />
      <span className="sr-only" aria-live="polite" aria-atomic="true" data-testid="document-search-live">
        {announcement}
      </span>
      {showDropdown && (
        showError ? (
          <div className="doc-search__dropdown" data-testid="document-search-dropdown">
            {scopeNest && (
              <DocumentSearchScope
                code={scopeNest.code || scopeNest.slug.toUpperCase()}
                name={scopeNest.name}
                onClear={() => setScopeCleared(true)}
              />
            )}
            <DocumentSearchError onRetry={search.refetch} />
          </div>
        ) : showEmpty ? (
          <div className="doc-search__dropdown" data-testid="document-search-dropdown">
            {scopeNest && (
              <DocumentSearchScope
                code={scopeNest.code || scopeNest.slug.toUpperCase()}
                name={scopeNest.name}
                onClear={() => setScopeCleared(true)}
              />
            )}
            <DocumentSearchEmpty
              query={search.query}
              fullSearchHref={fullSearchPath(search.query)}
              onNavigate={(p) => { setOpen(false); onNavigate(p); }}
            />
          </div>
        ) : (
          <DocumentSearchDropdown
            id={DOCUMENT_SEARCH_LISTBOX_ID}
            results={search.results}
            focusedIndex={focusedIndex}
            onSelect={select}
            onFocusIndex={setFocusedIndex}
            onLabelFilter={(kind, v) => {
              // The dropdown has no filter state of its own: a chip opens the
              // full-page search with that filter (the page owns the strip).
              setOpen(false);
              onNavigate(fullSearchPath(search.query, { kind, value: v }));
            }}
            header={
              scopeNest ? (
                <DocumentSearchScope
                  code={scopeNest.code || scopeNest.slug.toUpperCase()}
                  name={scopeNest.name}
                  onClear={() => setScopeCleared(true)}
                />
              ) : undefined
            }
          />
        )
      )}
    </div>
  );
}
